import { useQuery } from '@tanstack/react-query';

const CONTROL_PLANE_URL =
  import.meta.env.VITE_CONTROL_PLANE_URL || 'https://control.zaviago.com';

export interface FeatureIntroBenefit {
  icon: string;
  title: string;
  description: string;
}

export interface FeatureIntroData {
  feature_key: string;
  title: string;
  subtitle: string;
  icon: string;
  contact_email: string;
  benefits: FeatureIntroBenefit[];
}

/**
 * Fetch feature intro content from Control Plane.
 * Uses route path with fallback hierarchy on backend.
 */
async function fetchFeatureIntro(routePath: string): Promise<FeatureIntroData | null> {
  const cleanPath = routePath.replace(/^\//, '');

  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.feature_intro.get_feature_intro?route_path=${encodeURIComponent(cleanPath)}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
    }
  );

  if (!response.ok) {
    return null;
  }

  const data = await response.json();
  return data.message || null;
}

/**
 * Hook to fetch feature intro content for a given route.
 * Returns null if no content is configured (frontend shows default fallback).
 */
export function useFeatureIntro(routePath: string) {
  return useQuery({
    queryKey: ['featureIntro', routePath],
    queryFn: () => fetchFeatureIntro(routePath),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: false,
  });
}
